import { useQuery } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { UsersApi } from "@/openapi/generated";
import type { ScrollUserRequest, SearchQueryRequest } from "@/openapi/generated";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";

interface IUseUsersSearchPostProps {
  page?: number;
  pageSize?: number;
  active?: boolean;
  lastSelectedId?: number | null;
  searchQueryRequest?: SearchQueryRequest | null;
}

export const useUsersSearchPost = ({
  page = 1,
  pageSize = 100,
  active,
  lastSelectedId,
  searchQueryRequest,
}: IUseUsersSearchPostProps = {}) => {
  const accessToken = useAccessToken();

  const { data, isError, isLoading, isSuccess, isPending, refetch } = useQuery({
    queryKey: [QueryKeys.UsersScrollGet, "search", page, pageSize, active, lastSelectedId, searchQueryRequest],
    queryFn: () => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(UsersApi, accessToken);
      const scrollUserRequest: ScrollUserRequest = {
        studentLocationIds: [],
        studentSeasonIds: [],
        locationIds: [],
        roleNames: [],
        active: active ?? null,
        searchQuery: searchQueryRequest ?? null,
      };
      return service.usersScrollPost({
        scrollUserRequest,
        page,
        pageSize,
        lastSelectedId: lastSelectedId ?? undefined,
        active,
      });
    },
    enabled: !!accessToken && !!searchQueryRequest,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  return {
    data,
    isError,
    isLoading,
    isSuccess,
    isPending,
    refetch,
  };
};
